import {
  RedirectToCreateOrganization,
  RedirectToOrganizationProfile,
  useOrganization,
  useOrganizationList,
  useUser,
} from '@clerk/clerk-react';

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OnboardingPage from './OnboardingPage';
import { overviewPathForOrgType, readActiveOrg } from '../overviewPath';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { claimVerifierFirm } from 'modules/Verifier/api/verifierFirmApi';
import http from 'helpers/requests';
import { useFreemium } from 'modules/Common/freemium/FreemiumContext';
import { useCheckOnboarding, useTrialStatus } from 'modules/Onboarding/hooks';
import { identifyUser } from 'helpers/mixpanel';
import { useBranding } from '../hooks/useBranding';
import defaultLogo from 'assets/icons/logo.png';
import { getSafeRedirectUrl } from 'modules/Auth/redirectUrl';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import PermissionDenied from 'modules/Common/layout/PermissionDenied';
import PermissionsErrorState from 'modules/Common/permissions/PermissionsErrorState';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';
import { isFreeSupplier, SUPPLIER_HOME_PATH } from 'modules/FreeSupplier/planAccess';
import { isSupplierInvitationPath } from 'modules/FreeSupplier/routes';
import { destinationForPermissions, isPlanDenied } from '../workspaceNavigation';
import { preloadPageChunk } from '../config/routeChunks';
import { isAuditPath } from 'modules/Audit/utils/isAuditPath';

/**
 * Resolves true when the claim joined a firm AND activated it (the user had no workspace). Never
 * rejects: a failed claim must not strand a user on the loader. Once per session; an effect re-run
 * while the claim is in flight (activating the org re-runs it) awaits the same answer instead of
 * reading "already claimed" and falling through to onboarding.
 */
const claims = new Map();
function claimVerifierFirmOnce(userId, hasOrganization, setActive) {
  if (claims.has(userId)) return claims.get(userId);
  const key = `verifier-claim:${userId}`;
  try {
    if (sessionStorage.getItem(key)) return Promise.resolve(false);
    sessionStorage.setItem(key, '1');
  } catch {
    return Promise.resolve(false);
  }
  const claim = (async () => {
    try {
      const result = await claimVerifierFirm();
      if (result?.status !== 'joined' || !result.organizationId || hasOrganization) return false;
      await setActive({ organization: result.organizationId });
      await http.refreshClerkToken();
      return true;
    } catch {
      return false;
    }
  })();
  claims.set(userId, claim);
  return claim;
}

export default function InitPage(props) {
  const history = useHistory();
  const { t } = useTranslation('app');
  const params = new URLSearchParams(window.location.search);
  let paymentCompleted = Boolean(params.get('paymentComplete'));
  const [isInvited, setIsInvited] = useState(false);
  const [hasIdentifiedUser, setHasIdentifiedUser] = useState(false);

  let { isLoaded, organization } = useOrganization();
  const { user, isLoaded: isUserLoaded } = useUser();
  const { me, status: permissionsStatus } = usePermissionsContext();
  const brand = useBranding();
  const logo = brand.logoUrl || defaultLogo;
  const { userMemberships, setActive } = useOrganizationList({
    userMemberships: { pageSize: 10 },
  });

  const { triggerLimitExceeded } = useFreemium();

  // New onboarding check using React Query hook
  const {
    data: onboardingData,
    isLoading: isCheckingOnboarding,
    error: onboardingError,
  } = useCheckOnboarding();

  // Check trial status (only when user has organization)
  const {
    data: trialStatus,
    isLoading: isLoadingTrial,
    error: trialError,
  } = useTrialStatus({
    enabled: !!onboardingData?.hasOrganization,
  });

  // If the user was sent here from /sign-in?redirect_url=..., honor that
  // intended destination instead of dropping them on the default overview.
  const redirectUrl = getSafeRedirectUrl();

  // Resolve the destination from Clerk's authoritative active org, NOT the
  // useOrganization() hook. /init is where a post-org-switch bounce lands, and
  // at that moment the hook still reports the PREVIOUS org — so the old
  // "anything that isn't declarant/consultant is an operator" fallback sent a
  // freshly-switched declarant to /operator-overview, whose org-type gate
  // bounced it to /not-found before the hook caught up and corrected it. That
  // is the transient 404 users saw when switching operator → declarant.
  //
  // A null path means the type isn't known yet; navigating on a guess is what
  // caused the bug, so callers below simply don't navigate until it resolves.
  //
  // Free-supplier workspaces are Operator-type orgs whose plan (from
  // /permissions/me — the sole plan authority) decides the landing page, so
  // the plan branches take precedence over the org-type mapping. The effect
  // below defers while permissionsStatus === 'loading', so `me` is settled
  // before any navigation happens.
  const activeOrg = readActiveOrg(organization);

  // A free-tier plan without the Journey entitlement has no landing page it is
  // allowed to reach. That used to be expressed as `url = '/not-found'` and a
  // history.replace — an irreversible navigation, so a plan that resolved a beat
  // later left the user stranded on the 404. It is a DENIAL, so it renders in
  // place below and never navigates.
  // See docs/features/denial-never-navigates/00-OVERVIEW.md (N1, site 7).
  const planDenied = isPlanDenied({ me, redirectUrl });

  let url;
  if (redirectUrl) {
    url = redirectUrl;
  } else if (isFreeSupplier(me)) {
    url = SUPPLIER_HOME_PATH;
  } else if (planDenied) {
    // Null means "do not navigate" — the same contract the unresolved-org-type
    // branch below already relies on.
    url = null;
  } else {
    url = overviewPathForOrgType(activeOrg?.publicMetadata?.type);
  }

  // Start the landing page's chunk as soon as the destination is known, so it
  // downloads while the onboarding and permission checks below are still in
  // flight rather than after the redirect. A no-op for paths without a chunk.
  useEffect(() => {
    if (url) preloadPageChunk(url);
  }, [url]);

  // Check onboarding status and redirect accordingly
  useEffect(() => {
    const handleOnboarding = async () => {
      // Legacy audit links resolve the user's verifier firms before activating an org.
      // Sending them through customer onboarding can strand org-less audit users here.
      if (isUserLoaded && isAuditPath(redirectUrl)) {
        history.replace(redirectUrl);
        return;
      }
      // Wait for the Clerk user too: deciding before it loads skips the verifier claim below and
      // sends a brand-new firm member to onboarding, from which this effect never returns.
      if (!isCheckingOnboarding && isUserLoaded) {
        if (isSupplierInvitationPath(redirectUrl)) {
          history.replace(redirectUrl);
          return;
        }
        // Verifier firm domain join (verifier-firm-foundation phase 2 §4), once per session: a
        // person whose verified address is on a firm's domain joins it. With no other workspace
        // they go straight to it (or to the deep link they signed in for); otherwise it simply
        // appears in the org switcher.
        if (user?.id && (await claimVerifierFirmOnce(user.id, Boolean(organization), setActive))) {
          history.replace(redirectUrl || '/verifier');
          return;
        }
        if (organization && permissionsStatus === 'loading') return;
        if (organization && permissionsStatus === 'error') return;
        // If there's an error or no organization, redirect to onboarding
        if (onboardingError || !onboardingData?.hasOrganization) {
          console.log('No organization found, redirecting to onboarding');
          history.replace('/onboarding/type-selection');
        } else if (onboardingData?.hasOrganization && organization) {
          // User has organization, redirect based on organization type.
          // `url` is null while the org type is still unresolved — stay on the
          // loader rather than guessing a type-gated overview that would 404.
          if (url) {
            console.log('Organization found, redirecting to:', url);
            history.replace(url);
          } else if (!planDenied) {
            // publicMetadata arrives with the org resource, so a missing type
            // means the org genuinely has none — there is no correct overview to
            // send it to. Surfaced rather than silently guessed (the old
            // operator-overview default 404'd for declarants) so a mis-provisioned
            // org is diagnosable instead of just looking like a hung loader.
            //
            // `planDenied` is excluded: it ALSO leaves `url` null, but for a
            // workspace whose org type is perfectly fine. Logging it here
            // reported a plan denial as a mis-provisioned org, on every dep
            // change — noise that actively misleads whoever reads it next.
            console.error(
              'Active organization has no publicMetadata.type — cannot resolve a landing page.',
              activeOrg?.id,
            );
          }
        } else if (onboardingData.hasOrganization && !organization) {
          // Backend says the user has an org but Clerk has no active org set.
          // Activate it, then navigate using Clerk's imperative resource rather
          // than waiting for useOrganization() to re-render — that hook can lag
          // behind setActive, which is exactly why this used to hard-reload.
          await setActive({ organization: onboardingData.organization.id });
          // Force-mint a token for the org we just activated. Clerk caches the
          // pre-org (org-less) token for ~60s after setActive, and getClerkToken()
          // won't re-mint on its own once the cached orgId stops changing — so
          // without this the backend keeps getting a mis-scoped token and rejects
          // it. Mirrors the org-switch refresh in OrgBoundary (_app.jsx).
          await http.refreshClerkToken();
          const activeType = window.Clerk?.organization?.publicMetadata?.type;
          let dest = null;
          if (activeType === OrganizationType.Operator) {
            // Operator-type orgs may be free-supplier workspaces — the plan
            // (from /permissions/me, the sole plan authority) decides between
            // /supplier and /operator-overview, never the org type alone.
            const permissions = await http.get('/permissions/me');
            // destinationForPermissions THROWS for any plan with no reachable
            // home. That is BROADER than `planDenied` above: it also fires for
            // a paid plan missing `full_platform`, which is not a free-tier
            // denial at all. Either way this must not become an unhandled
            // rejection that parks the user on the loader — and the throw is
            // load-bearing elsewhere (useSupplierInvitation turns it into a
            // retryable_error), so it stays and we catch it here.
            //
            // dest = null uses the existing "do not navigate" contract. The
            // effect re-runs once useOrganization() catches up, and whichever
            // surface owns that plan then renders it: PermissionDenied for the
            // free-tier case, WorkspacePlanBoundary for the rest.
            try {
              dest = destinationForPermissions(permissions.data);
            } catch {
              dest = null;
            }
          } else {
            dest = overviewPathForOrgType(activeType);
          }
          // Unknown type → don't guess. This effect re-runs when the hook picks
          // up the org we just activated, and the branch above then navigates
          // with a resolved destination.
          if (dest) history.replace(dest);
        }
      }
    };

    handleOnboarding();
  }, [
    onboardingData,
    isCheckingOnboarding,
    isUserLoaded,
    user?.id,
    onboardingError,
    organization,
    url,
    planDenied,
    history,
    redirectUrl,
    permissionsStatus,
  ]);

  // Check trial expiration immediately after login
  useEffect(() => {
    if (isAuditPath(redirectUrl)) return;
    if (!isLoadingTrial && trialStatus && onboardingData?.hasOrganization) {
      console.log('Trial status check:', {
        isExpired: trialStatus.isExpired,
        hasActiveTrial: trialStatus.hasActiveTrial,
        daysRemaining: trialStatus.daysRemaining,
      });

      // Check if trial is expired (regardless of hasActiveTrial status)
      if (trialStatus.isExpired) {
        console.log('Trial has expired, redirecting to trial expired page');
        history.replace('/trial-expired');
      }
    }
  }, [trialStatus, isLoadingTrial, onboardingData, history, redirectUrl]);

  // Identify user in Mixpanel when user and organization data is available
  useEffect(() => {
    if (user && organization && !hasIdentifiedUser) {
      identifyUser(user, organization);
      setHasIdentifiedUser(true);
    }
  }, [user, organization, hasIdentifiedUser]);

  useEffect(() => {
    const checkLimit = async () => {
      await triggerLimitExceeded();
    };
    if (paymentCompleted) {
      checkLimit();
    }
  }, []);

  useEffect(() => {
    if (isAuditPath(redirectUrl)) return;
    const loadIsInvited = async () => {
      try {
        const res = await http.get('/requests/is_invited');
        setIsInvited(Boolean(res.data?.isInvited));
      } catch {
        setIsInvited(false);
      }
    };
    loadIsInvited();
  }, [userMemberships, redirectUrl]);

  const getLoading = () => (
    <CgPageLoader
      variant="fullscreen"
      logo={logo}
      brandName={brand.name}
      primaryColor={brand.primaryColor}
      ariaLabel={t('routeLoader.ariaLabel', 'Loading your workspace')}
    />
  );

  if (!isLoaded) {
    return getLoading();
  }

  // OLD PAYMENT FLOW - Disabled in favor of new onboarding flow
  // if (!organization && !paymentCompleted) {
  //   let stripeOnboarding = '/payment';
  //   history.push(stripeOnboarding);
  //   return;
  // }

  // Show loading while checking onboarding status
  if (isCheckingOnboarding) {
    return getLoading();
  }
  if (organization && permissionsStatus === 'loading') {
    return getLoading();
  }
  if (organization && permissionsStatus === 'error') {
    return <PermissionsErrorState />;
  }
  // N5: never a denial on an unresolved payload. The guards above are gated on
  // `organization &&` — the LAGGING useOrganization() hook — while `me` comes
  // from the authoritative provider, so they do not by themselves prove the
  // payload settled. And `me` is populated during the provisioning grace window
  // (PermissionsProvider returns `data || null` while reporting 'loading'), so a
  // provisional snapshot can carry a free-tier plan with empty entitlements and
  // satisfy `planDenied` before the real one lands. Condition on the status
  // itself. Guarding the LOADERS this way instead would brick org-less
  // onboarding: status is 'loading' forever while the query is disabled.
  if (permissionsStatus === 'ready' && planDenied) {
    return <PermissionDenied />;
  }
  return (
    <>
      {!organization && !isInvited && getLoading()}
      {((organization && !organization?.publicMetadata?.onboarded) || isInvited) && (
        <OnboardingPage />
      )}
      {/* `url` is null until the org type resolves; pushing it then would send
          the router to a garbage location. The effect above owns navigation for
          the resolved case. */}
      {organization?.publicMetadata.onboarded && url && history.push(url)}
    </>
  );
}
